import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Fade } from '@mui/material'
import React from 'react'
import * as Styled from './styles'

interface ScrollIndicatorProps {
  contentRef: React.RefObject<HTMLDivElement>
  isOpen?: boolean // Optional prop to re-run effect when container opens/closes
}

const ScrollIndicator: React.FC<ScrollIndicatorProps> = ({ contentRef, isOpen }) => {
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false)

  React.useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = contentRef.current
        // Add a small buffer (20px) to account for potential rounding issues
        const hasMoreContent = scrollHeight > clientHeight + 20
        // Check if we're at the bottom with a small threshold
        const isAtBottom = Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 5
        const isNotAtBottom = !isAtBottom
        
        setShowScrollIndicator(hasMoreContent && isNotAtBottom)
      }
    }

    const contentElement = contentRef.current
    if (contentElement) {
      // Check immediately when the container opens
      checkScroll()
      
      // Also check after a small delay to ensure content is fully rendered
      const timeoutId = setTimeout(checkScroll, 100)
      
      contentElement.addEventListener('scroll', checkScroll)
      
      // Add resize observer to handle window/container size changes
      const resizeObserver = new ResizeObserver(checkScroll)
      resizeObserver.observe(contentElement)

      return () => {
        contentElement.removeEventListener('scroll', checkScroll)
        resizeObserver.disconnect()
        clearTimeout(timeoutId)
      }
    }
  }, [contentRef, isOpen]) // Re-run when container opens/closes

  return (
    <Fade in={showScrollIndicator}>
      <Styled.ScrollIndicator>
        <KeyboardArrowDownIcon sx={{ fontSize: 32, color: 'rgba(0, 0, 0, 0.54)' }} />
      </Styled.ScrollIndicator>
    </Fade>
  )
}

export default ScrollIndicator 